import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Shield, Delete, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface AuthPinModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (pin: string) => Promise<void>;
  title?: string;
  description?: string;
  loading?: boolean;
}

export function AuthPinModal({
  open,
  onClose,
  onConfirm,
  title = "Autorização Necessária",
  description = "Digite o PIN de autorização para continuar.",
  loading = false,
}: AuthPinModalProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPin("");
      setError("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleDigit = (digit: string) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setError("");
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError("");
  };

  const handleConfirm = async () => {
    if (pin.length < 4) {
      setError("O PIN deve ter pelo menos 4 dígitos.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(pin);
      setPin("");
    } catch (err: any) {
      const msg = err?.message || err?.data?.message || "PIN incorreto. Tente novamente.";
      setError(msg);
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setPin("");
      setError("");
      onClose();
    }
  };

  const isLoading = loading || submitting;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-5 w-5 text-amber-500" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* PIN display */}
        <div className="flex justify-center gap-3 my-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all",
                i < pin.length
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-muted-foreground/30 bg-muted/30"
              )}
            >
              {i < pin.length ? "●" : ""}
            </div>
          ))}
        </div>

        {/* Hidden input for mobile keyboard */}
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          value={pin}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, "").slice(0, 6);
            setPin(val);
            setError("");
          }}
          className="sr-only"
          onKeyDown={e => {
            if (e.key === "Enter" && pin.length >= 4) handleConfirm();
          }}
        />

        {/* Numeric keypad */}
        <div className="grid grid-cols-3 gap-2">
          {["1","2","3","4","5","6","7","8","9"].map(d => (
            <Button
              key={d}
              variant="outline"
              className="h-12 text-lg font-semibold"
              onClick={() => handleDigit(d)}
              disabled={isLoading}
            >
              {d}
            </Button>
          ))}
          <div /> {/* empty cell */}
          <Button
            variant="outline"
            className="h-12 text-lg font-semibold"
            onClick={() => handleDigit("0")}
            disabled={isLoading}
          >
            0
          </Button>
          <Button
            variant="outline"
            className="h-12"
            onClick={handleDelete}
            disabled={isLoading || pin.length === 0}
          >
            <Delete className="h-5 w-5" />
          </Button>
        </div>

        {/* Error message */}
        {error && (
          <p className="text-sm text-destructive text-center mt-1">{error}</p>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            className="flex-1"
            onClick={handleConfirm}
            disabled={isLoading || pin.length < 4}
          >
            {isLoading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verificando...</>
            ) : (
              "Confirmar"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
